import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import { toast, Toaster } from "react-hot-toast";
import Pagination from "components/pagination";
import { listServiceReviewSummaries } from "../../reviews/reviewApi";

const ServiceReviewsList = () => {
  const navigate = useNavigate();
  const antIcon = <LoadingOutlined style={{ fontSize: 60 }} spin />;

  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);

  useEffect(() => {
    const fetchServices = async () => {
      setLoading(true);
      try {
        const result = await listServiceReviewSummaries();

        if (result?.status === 200) {
          setServices(Array.isArray(result?.data) ? result.data : []);
        } else {
          toast.error(result?.message || "Failed to fetch services");
        }
      } catch (error) {
        console.error("Error fetching services:", error);
        toast.error("Failed to fetch services");
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const filteredServices = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return services;

    return services.filter((service) => {
      const serviceId = String(service?.service_id || service?.id || "").toLowerCase();
      const serviceName = String(service?.service_name || service?.name || "").toLowerCase();
      const typeName = String(service?.service_type_name || service?.type_name || "").toLowerCase();
      return serviceId.includes(query) || serviceName.includes(query) || typeName.includes(query);
    });
  }, [services, searchTerm]);

  const totalRecords = filteredServices.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedServices = filteredServices.slice(startIndex, endIndex);
  const displayedCount = totalRecords === 0 ? 0 : Math.min(totalRecords, endIndex);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Toaster position="top-center" reverseOrder={false} />

      {loading ? (
        <div className="flex h-[75vh] w-full items-center justify-center">
          <Spin indicator={antIcon} />
        </div>
      ) : (
        <div className="mt-6 rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-6 py-4">
            <h2 className="text-base font-semibold text-gray-800">Service Reviews</h2>
            <p className="mt-1 text-xs text-gray-500">Service review summary list from review APIs.</p>
          </div>

          <div className="px-6 py-4">
            <input
              type="text"
              className="block w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 lg:w-96"
              placeholder="Search service by id, name or type"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="mx-4 mb-3 rounded-md border border-gray-100">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              recordsPerPage={recordsPerPage}
              onRecordsPerPageChange={(val) => {
                setRecordsPerPage(val);
                setCurrentPage(1);
              }}
              totalRecords={totalRecords}
              count={displayedCount}
              showFirstLastControls
              compact
            />
          </div>

          <div className="mx-4 mb-4 overflow-x-auto">
            <table className="w-full text-left text-xs text-gray-600">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-700">
                <tr>
                  <th className="px-3 py-2.5">Sr No</th>
                  <th className="px-3 py-2.5">Service ID</th>
                  <th className="px-3 py-2.5">Service Name</th>
                  <th className="px-3 py-2.5">Type</th>
                  <th className="px-3 py-2.5">Total Reviews</th>
                  <th className="px-3 py-2.5">Average Rating</th>
                  <th className="px-3 py-2.5">Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedServices.length > 0 ? (
                  paginatedServices.map((service, index) => (
                    <tr
                      key={service?.service_id ?? service?.id ?? index}
                      className="border-b bg-white hover:bg-gray-50"
                    >
                      <td className="whitespace-nowrap px-3 py-2">{startIndex + index + 1}</td>
                      <td className="whitespace-nowrap px-3 py-2">
                        {service?.service_id || service?.id || "-"}
                      </td>
                      <td className="px-3 py-2">{service?.service_name || service?.name || "-"}</td>
                      <td className="px-3 py-2">
                        {service?.service_type_name || service?.type_name || "-"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        {service?.total_reviews ?? service?.total ?? 0}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        {service?.average_rating ?? service?.avg_rating ?? service?.average ?? 0}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/service-reviews/${service?.service_id || service?.id}`, {
                              state: { service },
                            })
                          }
                          className="rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="py-6 text-center text-sm text-gray-500">
                      No services found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mx-4 mb-4 rounded-md border border-gray-100">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              recordsPerPage={recordsPerPage}
              onRecordsPerPageChange={(val) => {
                setRecordsPerPage(val);
                setCurrentPage(1);
              }}
              totalRecords={totalRecords}
              count={displayedCount}
              showFirstLastControls
              compact
            />
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ServiceReviewsList;
